import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MapPin, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocalClearanceSection } from "@/components/local-clearance-section";
import { LocalCleaningSection } from "@/components/local-cleaning-section";
import { LocalDismantlingSection } from "@/components/local-dismantling-section";
import { RegionalServiceLinks } from "@/components/regional-service-links";
import { directoryRegions } from "@/data/directory-pages";
import { cityGeo, getCityPage } from "@/data/city-pages";
import { tradePages } from "@/data/trade-pages";
import { breadcrumbLd, geoMeta, jsonLdScript, SITE_URL } from "@/lib/json-ld";

export const Route = createFileRoute("/handwerker/$stadt")({
  staticData: { sitemap: true },
  loader: ({ params }) => {
    const city = getCityPage(params.stadt);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData }) => {
    const city = loaderData?.city;
    const title = city
      ? `Handwerker & Wohnungsräumung ${city.name} | Begner`
      : "Handwerker finden | Begner";
    const description = city
      ? `Handwerker und Reinigungsfirmen in ${city.name}, Kanton ${city.cantonName}: Wohnungsräumung, Wohnungsreinigung und Hauswartung. Offerten kostenlos anfragen.`
      : "Handwerker in der Schweiz finden und Offerten vergleichen.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(city && cityGeo[city.slug]
          ? geoMeta(
              cityGeo[city.slug].lat,
              cityGeo[city.slug].lng,
              `CH-${city.canton.toUpperCase()}`,
              city.name,
            )
          : []),
      ],
      scripts: city
        ? [
            jsonLdScript({
              "@context": "https://schema.org",
              "@type": "Service",
              name: `Handwerker in ${city.name}`,
              url: `${SITE_URL}/handwerker/${city.slug}`,
              provider: { "@type": "Organization", name: "Begner", url: SITE_URL },
              areaServed: {
                "@type": "City",
                name: city.name,
                containedInPlace: {
                  "@type": "AdministrativeArea",
                  name: `Kanton ${city.cantonName}`,
                },
                ...(cityGeo[city.slug]
                  ? {
                      geo: {
                        "@type": "GeoCoordinates",
                        latitude: cityGeo[city.slug].lat,
                        longitude: cityGeo[city.slug].lng,
                      },
                    }
                  : {}),
              },
            }),
            jsonLdScript(
              breadcrumbLd([
                { name: "Startseite", path: "/" },
                { name: "Handwerker", path: "/auftragnehmer" },
                { name: city.name, path: `/handwerker/${city.slug}` },
              ]),
            ),
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Region nicht gefunden</h1>
      <Button asChild className="mt-6 rounded-xl">
        <Link to="/auftragnehmer">Alle Auftragnehmer</Link>
      </Button>
    </div>
  ),
  component: CityPageView,
});

function CityPageView() {
  const { city } = Route.useLoaderData();
  const directoryRegion = directoryRegions.find((region) => region.canton === city.canton)!;
  const steps = [
    "Projekt kostenlos beschreiben, mit Fotos und Zeitraum.",
    `Betriebe aus ${city.name} und Umgebung melden sich mit Offerten.`,
    "Profile, Bewertungen und Preise vergleichen und frei entscheiden.",
  ];

  return (
    <>
      <section className="border-b border-border bg-surface py-14 md:py-20">
        <div className="container-page max-w-4xl">
          <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep">
            <MapPin className="h-4 w-4" /> Kanton {city.cantonName}
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight md:text-5xl">
            Handwerker in {city.name} finden
          </h1>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">{city.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl">
              <Link to="/auftrag-einstellen">
                Anfrage kostenlos eingeben <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl bg-card">
              <Link to="/handwerkerverzeichnis/$region" params={{ region: directoryRegion.slug }}>
                Auftragnehmer in {city.cantonName}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="container-page grid max-w-4xl gap-10 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold">Häufige Projekte in {city.name}</h2>
            <ul className="mt-5 space-y-3">
              {city.topics.map((topic) => (
                <li key={topic} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-brand" />
                  {topic}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-6 text-muted-foreground">{city.context}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h2 className="font-display text-2xl font-bold">So funktioniert es</h2>
            <ol className="mt-5 space-y-4">
              {steps.map((step, index) => (
                <li key={step} className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-bold text-brand-deep">
                    {index + 1}
                  </span>
                  <span className="text-sm leading-6">{step}</span>
                </li>
              ))}
            </ol>
            <Button asChild variant="link" className="mt-4 px-0">
              <Link to="/preise">Richtpreise ansehen</Link>
            </Button>
          </div>
        </div>
      </section>

      <RegionalServiceLinks region={directoryRegion} />
      <LocalClearanceSection location={city.name} cantonName={city.cantonName} />
      <LocalDismantlingSection location={city.name} cantonName={city.cantonName} />
      <LocalCleaningSection location={city.name} cantonName={city.cantonName} />

      <section className="border-t border-border bg-surface py-12">
        <div className="container-page max-w-4xl">
          <h2 className="font-display text-xl font-bold">Gewerke in {city.name}</h2>
          <div className="mb-10 mt-5 flex flex-wrap gap-3">
            {tradePages.map((trade) => (
              <Link
                key={trade.slug}
                to="/handwerker/$stadt/$gewerk"
                params={{ stadt: city.slug, gewerk: trade.slug }}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
              >
                <Wrench className="h-4 w-4 text-brand" /> {trade.name}
              </Link>
            ))}
          </div>
          <h2 className="font-display text-xl font-bold">Handwerker in der Nähe</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            {city.nearby.map((slug) => {
              const other = getCityPage(slug);
              if (!other) return null;
              return (
                <Link
                  key={slug}
                  to="/handwerker/$stadt"
                  params={{ stadt: slug }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
                >
                  <MapPin className="h-4 w-4 text-brand" /> {other.name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
